"use client";

import { useState, useEffect } from "react";
import { FileText, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Textarea } from "@/app/_module/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { useToast } from "@/app/_module/hooks/use-toast";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

interface AgencyCarrier {
  carrierId: string;
  carrierSlug: string;
  carrierName: string;
  isEnabled: boolean;
}

interface DocumentRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  prefilledCarrier?: string;
  prefilledDocumentType?: string;
  prefilledReason?: string;
}

export function DocumentRequestModal({
  isOpen,
  onClose,
  onSuccess,
  prefilledCarrier,
  prefilledDocumentType,
  prefilledReason,
}: DocumentRequestModalProps) {
  const { toast } = useToast();
  const { user } = useUserContext();
  const agencyId = user?.currentAgency?.agency?.id;

  // State for form fields
  const [carriers, setCarriers] = useState<AgencyCarrier[]>([]);
  const [selectedCarrierId, setSelectedCarrierId] = useState<string>("");
  const [documentType, setDocumentType] = useState<string>("");
  const [requestReason, setRequestReason] = useState<string>("");
  const [priority, setPriority] = useState<string>("normal");

  // Loading and status states
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState<{
    carrierId?: string;
    documentType?: string;
    requestReason?: string;
  }>({});

  // Fetch user's appointed carriers when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCarriers();
      // Reset form when modal opens
      resetForm();
    }
  }, [isOpen]);

  // Set prefilled data when props change
  useEffect(() => {
    if (prefilledCarrier) {
      setSelectedCarrierId(prefilledCarrier);
    }
    if (prefilledDocumentType) {
      setDocumentType(prefilledDocumentType);
    }
    if (prefilledReason) {
      setRequestReason(prefilledReason);
    }
  }, [prefilledCarrier, prefilledDocumentType, prefilledReason, isOpen]);

  const fetchCarriers = async () => {
    try {
      setIsLoadingCarriers(true);
      // For agency members, ensure we only get their agency's carriers
      const url = agencyId 
        ? getApiUrl(`/agency-carriers?agencyId=${agencyId}`)
        : getApiUrl("/agency-carriers");
      
      const response = await handleApiCall(async () => {
        return await api.get({
          url,
          auth: true,
        });
      });

      if (response) {
        let carriersList: AgencyCarrier[] = Array.isArray(response) ? response : [];
        
        // Filter by agency if agencyId is provided
        if (agencyId) {
          carriersList = carriersList.filter((carrier: any) => 
            carrier.agencyId === agencyId || !carrier.agencyId
          );
        }
        
        // Filter only enabled carriers
        const enabledCarriers: AgencyCarrier[] = carriersList.filter(
          (carrier: AgencyCarrier) => carrier.isEnabled
        );
        setCarriers(enabledCarriers);
      }
    } catch (error) {

      toast({
        title: "Error",
        description: "Failed to load carriers. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCarriers(false);
    }
  };

  const resetForm = () => {
    setSelectedCarrierId("");
    setDocumentType("");
    setRequestReason("");
    setPriority("normal");
    setErrors({});
    setIsSuccess(false);
  };

  const validateForm = (): boolean => {
    const newErrors: {
      carrierId?: string;
      documentType?: string;
      requestReason?: string;
    } = {};

    if (!selectedCarrierId) {
      newErrors.carrierId = "Please select a carrier";
    }

    if (!documentType.trim()) {
      newErrors.documentType = "Document type is required";
    }

    if (!requestReason.trim()) {
      newErrors.requestReason = "Request reason is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await handleApiCall(async () => {
        return await api.post({
          url: getApiUrl("/api/document-requests"),
          body: {
            carrierId: selectedCarrierId,
            documentType: documentType.trim(),
            requestReason: requestReason.trim(),
            priority,
          },
        });
      });

      if (response) {
        setIsSuccess(true);
        toast({
          title: "Request Submitted",
          description: "Your document request has been submitted successfully.",
        });

        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }

        // Close modal after 2 seconds to show success message
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error: any) {

      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          "Failed to submit document request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  // Success state view
  if (isSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <ResponsiveDialogContent size="md">
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <div className="mb-4 rounded-full bg-green-100 p-3">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="mb-2 text-2xl font-semibold text-gray-900">
              Request Submitted!
            </h2>
            <p className="mb-4 text-sm text-gray-600">
              Your document request has been successfully submitted.
            </p>
            <div className="w-full rounded-lg bg-blue-50 p-4 border border-blue-200">
              <p className="text-sm text-blue-900">
                <strong>What happens next?</strong>
              </p>
              <p className="mt-2 text-sm text-blue-800">
                Admins have been notified of your request and will process it as
                soon as possible. You will be notified once the document is
                available.
              </p>
            </div>
          </div>
        </ResponsiveDialogContent>
      </Dialog>
    );
  }

  // Main form view
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <ResponsiveDialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Request Missing Document
          </DialogTitle>
          <DialogDescription>
            Submit a request for a missing carrier document. Admins will be
            notified and will process your request.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Carrier Selection */}
          <div className="space-y-2">
            <Label htmlFor="carrier">
              Carrier <span className="text-red-500">*</span>
            </Label>
            {isLoadingCarriers ? (
              <div className="flex items-center justify-center h-10 border rounded-md bg-gray-50">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">
                  Loading carriers...
                </span>
              </div>
            ) : carriers.length === 0 ? (
              <div className="flex items-center gap-2 p-3 border rounded-md bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <span className="text-sm text-amber-800">
                  No carriers found. Please configure carriers in settings first.
                </span>
              </div>
            ) : (
              <Select
                value={selectedCarrierId}
                onValueChange={(value) => {
                  setSelectedCarrierId(value);
                  setErrors((prev) => ({ ...prev, carrierId: undefined }));
                }}
              >
                <SelectTrigger
                  id="carrier"
                  className={errors.carrierId ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select a carrier" />
                </SelectTrigger>
                <SelectContent>
                  {carriers.map((carrier) => (
                    <SelectItem key={carrier.carrierId} value={carrier.carrierId}>
                      {carrier.carrierName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {errors.carrierId && (
              <p className="text-sm text-red-500">{errors.carrierId}</p>
            )}
          </div>

          {/* Document Type */}
          <div className="space-y-2">
            <Label htmlFor="documentType">
              Document Type <span className="text-red-500">*</span>
            </Label>
            <Input
              id="documentType"
              type="text"
              placeholder="Commission Statement, Contract, Policy Form"
              value={documentType}
              onChange={(e) => {
                setDocumentType(e.target.value);
                setErrors((prev) => ({ ...prev, documentType: undefined }));
              }}
              className={errors.documentType ? "border-red-500" : ""}
            />
            {errors.documentType && (
              <p className="text-sm text-red-500">{errors.documentType}</p>
            )}
          </div>

          {/* Request Reason */}
          <div className="space-y-2">
            <Label htmlFor="requestReason">
              Request Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="requestReason"
              placeholder="Please explain why you need this document..."
              value={requestReason}
              onChange={(e) => {
                setRequestReason(e.target.value);
                setErrors((prev) => ({ ...prev, requestReason: undefined }));
              }}
              className={`min-h-[100px] ${errors.requestReason ? "border-red-500" : ""}`}
            />
            {errors.requestReason && (
              <p className="text-sm text-red-500">{errors.requestReason}</p>
            )}
          </div>

          {/* Priority Selection */}
          <div className="space-y-2">
            <Label htmlFor="priority">Priority (Optional)</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger id="priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low - No rush</SelectItem>
                <SelectItem value="normal">Normal - Standard processing</SelectItem>
                <SelectItem value="high">High - Need soon</SelectItem>
                <SelectItem value="urgent">Urgent - Critical need</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Select the urgency level for this request
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || isLoadingCarriers || carriers.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Request"
            )}
          </Button>
        </DialogFooter>
      </ResponsiveDialogContent>
    </Dialog>
  );
}
